import { Eye, Compass, Users, BadgeCheck, Scale } from "lucide-react";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import CTASection from "../components/CTASection";
import { IMAGES, COMPLIANCE_TEXT } from "../lib/siteConfig";

const VALUES = [
  { icon: Eye, title: "Transparency", desc: "Clear, honest communication at every stage — no hidden charges, no unrealistic promises." },
  { icon: Compass, title: "Responsible Guidance", desc: "We guide customers towards options that fit their profile, not towards unnecessary products." },
  { icon: Users, title: "Customer First", desc: "Every enquiry receives personal attention from a dedicated support representative." },
  { icon: BadgeCheck, title: "Professionalism", desc: "A trained team that understands documentation, eligibility and institutional processes." },
];

const MODEL = [
  { title: "We listen", desc: "Understanding your requirement, profile and goals before suggesting any direction." },
  { title: "We guide", desc: "Explaining eligibility criteria, documentation and suitable options in plain language." },
  { title: "We connect", desc: "Coordinating with relevant banks and financial institutions so you apply through the right channel." },
];

export default function About() {
  return (
    <main>
      <PageHero
        testid="about-hero"
        eyebrow="About Us"
        titleLines={["Simplifying the Loan", "Journey for India"]}
        description="CREDNOVA SERVICES PRIVATE LIMITED is an Indian financial services company on a mission to make the loan application process simple, transparent and stress-free for individuals and businesses."
      />

      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-20 lg:pb-28" data-testid="about-mission">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div className="relative rounded-3xl overflow-hidden border border-[rgba(212,175,55,0.25)]">
              <img src={IMAGES.about} alt="CREDNOVA team at work" className="w-full h-[380px] sm:h-[460px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0D]/80 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="caption-gold mb-2">Our Mission</p>
                <p className="font-display text-lg sm:text-xl text-white font-medium leading-snug">
                  To simplify the loan application process and connect every eligible customer with the right financial institution.
                </p>
              </div>
            </div>
          </Reveal>
          <div>
            <Reveal>
              <p className="caption-gold mb-4">Who We Are</p>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white mb-6">
                A Facilitation Partner, Not a Lender
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-sm sm:text-base text-[#A1A1AA] leading-relaxed mb-5">
                We are a loan assistance and facilitation company. Our role is to help customers understand
                their options, prepare strong applications, and connect with banks and financial institutions
                whose products suit their needs.
              </p>
              <p className="text-sm sm:text-base text-[#A1A1AA] leading-relaxed mb-8">
                From first-time personal loan applicants to growing businesses seeking working capital, we
                bring structure, clarity and dedicated support to a process that often feels overwhelming.
              </p>
            </Reveal>
            <div className="space-y-5">
              {MODEL.map((m, i) => (
                <Reveal key={m.title} delay={0.15 + i * 0.08}>
                  <div className="flex gap-5 items-start" data-testid={`about-model-${i + 1}`}>
                    <span className="font-mono-brand text-xs text-[#D4AF37] mt-1 shrink-0">0{i + 1}</span>
                    <div>
                      <h3 className="font-display text-base font-medium text-white">{m.title}</h3>
                      <p className="text-sm text-[#A1A1AA] mt-1 leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-[#0C0C10] border-y border-[rgba(212,175,55,0.12)]" data-testid="about-values">
        <div className="max-w-7xl mx-auto">
          <Reveal className="mb-12">
            <p className="caption-gold mb-4">Our Values</p>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white">
              The Principles Behind Every Enquiry
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.09}>
                <div className="card-gold p-7 h-full" data-testid={`about-value-${i + 1}`}>
                  <v.icon size={24} className="text-[#D4AF37] mb-5" strokeWidth={1.5} />
                  <h3 className="font-display text-lg font-medium text-white mb-2">{v.title}</h3>
                  <p className="text-sm text-[#A1A1AA] leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15} className="mt-10">
            <div className="flex items-start gap-4 rounded-2xl border border-[rgba(212,175,55,0.25)] bg-[#121216] p-6">
              <Scale size={20} className="text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={1.5} />
              <p className="text-sm text-[#A1A1AA] leading-relaxed">{COMPLIANCE_TEXT}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection
        title="Let's Understand Your Requirement"
        subtitle="Share your details and our team will guide you towards suitable loan options from banks and financial institutions."
      />
    </main>
  );
}
